import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Users,
  DollarSign,
  Calendar,
  LogOut,
  CheckCircle,
  Clock,
  MessageSquare,
  Search,
  Trash2,
  ChevronDown,
  ChevronUp,
  Phone,
  Mail,
  Globe,
  CreditCard,
  XCircle,
  RefreshCw,
  FileText,
  Plus,
  Edit,
  Eye,
  Sparkles,
  Save,
  X,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("bookings");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  // Article editor state
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: "", slug: "", content: "", excerpt: "", featured_image: "",
    category: "kitesurfing", tags: "", meta_title: "", meta_description: "", status: "draft",
  });
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiKeywords, setAiKeywords] = useState("");
  const [savingArticle, setSavingArticle] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, contactsRes, articlesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthConfig()),
        axios.get(`${API}/bookings`, getAuthConfig()),
        axios.get(`${API}/contacts`, getAuthConfig()),
        axios.get(`${API}/articles?status=all`, getAuthConfig()),
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setContacts(contactsRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  // ===== ARTICLE FUNCTIONS =====
  const resetArticleForm = () => {
    setArticleForm({
      title: "", slug: "", content: "", excerpt: "", featured_image: "",
      category: "kitesurfing", tags: "", meta_title: "", meta_description: "", status: "draft",
    });
    setEditingArticle(null);
  };

  const openNewArticle = () => {
    resetArticleForm();
    setEditingArticle("new");
  };

  const openEditArticle = (article) => {
    setArticleForm({
      title: article.title, slug: article.slug, content: article.content,
      excerpt: article.excerpt || "", featured_image: article.featured_image || "",
      category: article.category || "kitesurfing", tags: (article.tags || []).join(", "),
      meta_title: article.meta_title || "", meta_description: article.meta_description || "",
      status: article.status || "draft",
    });
    setEditingArticle(article.id);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title || !articleForm.slug || !articleForm.content) {
      toast.error("Title, slug, and content are required");
      return;
    }
    setSavingArticle(true);
    try {
      const payload = {
        ...articleForm,
        tags: articleForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editingArticle === "new") {
        await axios.post(`${API}/articles`, payload, getAuthConfig());
        toast.success("Article created!");
      } else {
        await axios.patch(`${API}/articles/${editingArticle}`, payload, getAuthConfig());
        toast.success("Article updated!");
      }
      resetArticleForm();
      fetchData();
    } catch (err) {
      toast.error("Failed to save article");
    } finally {
      setSavingArticle(false);
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await axios.delete(`${API}/articles/${id}`, getAuthConfig());
      toast.success("Article deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleTogglePublish = async (article) => {
    const newStatus = article.status === "published" ? "draft" : "published";
    try {
      await axios.patch(`${API}/articles/${article.id}`, { status: newStatus }, getAuthConfig());
      toast.success(newStatus === "published" ? "Article published!" : "Article unpublished");
      fetchData();
    } catch { toast.error("Failed to update"); }
  };

  const handleAIGenerate = async () => {
    if (!aiTopic) { toast.error("Enter a topic"); return; }
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API}/articles/generate`, {
        topic: aiTopic, keywords: aiKeywords, tone: "informative", language: "en",
      }, getAuthConfig());
      const data = res.data;
      if (data.error) { toast.error("AI generation failed. Try again."); return; }
      setArticleForm({
        title: data.title || "", slug: data.slug || "",
        content: data.content || "", excerpt: data.excerpt || "",
        featured_image: data.featured_image || "", category: data.category || "kitesurfing",
        tags: (data.tags || []).join(", "), meta_title: data.meta_title || "",
        meta_description: data.meta_description || "", status: "draft",
      });
      setEditingArticle("new");
      toast.success("Article generated! Review and publish when ready.");
    } catch (err) {
      toast.error("AI generation failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    try {
      await axios.patch(
        `${API}/bookings/${bookingId}/status?status=${status}`,
        {},
        getAuthConfig()
      );
      toast.success(`Booking ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleUpdatePaymentStatus = async (bookingId, paymentStatus) => {
    try {
      await axios.patch(
        `${API}/bookings/${bookingId}/payment-status?payment_status=${paymentStatus}`,
        {},
        getAuthConfig()
      );
      toast.success(`Payment marked as ${paymentStatus}`);
      fetchData();
    } catch {
      toast.error("Failed to update payment status");
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, getAuthConfig());
      toast.success("Booking deleted");
      setExpandedBooking(null);
      fetchData();
    } catch {
      toast.error("Failed to delete booking");
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Delete this contact message?")) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}`, getAuthConfig());
      toast.success("Contact deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete contact");
    }
  };

  const filteredBookings = bookings.filter((b) => {
    const matchesSearch =
      !searchQuery ||
      b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.package_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.phone?.includes(searchQuery);
    const matchesStatus = statusFilter === "all" || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const filteredContacts = contacts.filter(
    (c) =>
      !searchQuery ||
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.message?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
      pending: "bg-amber-100 text-amber-800 border-amber-200",
      cancelled: "bg-red-100 text-red-800 border-red-200",
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const styles = {
      paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
      pending: "bg-amber-100 text-amber-800 border-amber-200",
      refunded: "bg-blue-100 text-blue-800 border-blue-200",
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Admin Dashboard
              </h1>
              <p className="text-sm text-gray-500">Dakhla Kite Planet</p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={fetchData}
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="refresh-btn"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Bookings</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.total_bookings || 0}</p>
                </div>
                <div className="bg-sky-50 p-2.5 rounded-lg">
                  <Calendar className="h-5 w-5 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pending</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1">{stats?.pending_bookings || 0}</p>
                </div>
                <div className="bg-amber-50 p-2.5 rounded-lg">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Confirmed</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">{stats?.confirmed_bookings || 0}</p>
                </div>
                <div className="bg-emerald-50 p-2.5 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Revenue</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.total_revenue?.toFixed(0) || 0}€</p>
                </div>
                <div className="bg-yellow-50 p-2.5 rounded-lg">
                  <DollarSign className="h-5 w-5 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Messages</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">{stats?.total_contacts || 0}</p>
                </div>
                <div className="bg-purple-50 p-2.5 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex gap-1">
              <button
                onClick={() => { setActiveTab("bookings"); setSearchQuery(""); }}
                data-testid="bookings-tab"
                className={`py-2.5 px-4 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === "bookings"
                    ? "bg-white border border-b-white border-gray-200 text-sky-600 -mb-px"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Users className="inline h-4 w-4 mr-1.5" />
                Bookings ({bookings.length})
              </button>
              <button
                onClick={() => { setActiveTab("contacts"); setSearchQuery(""); }}
                data-testid="contacts-tab"
                className={`py-2.5 px-4 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === "contacts"
                    ? "bg-white border border-b-white border-gray-200 text-sky-600 -mb-px"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <MessageSquare className="inline h-4 w-4 mr-1.5" />
                Messages ({contacts.length})
              </button>
              <button
                onClick={() => { setActiveTab("articles"); setSearchQuery(""); }}
                data-testid="articles-tab"
                className={`py-2.5 px-4 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === "articles"
                    ? "bg-white border border-b-white border-gray-200 text-sky-600 -mb-px"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FileText className="inline h-4 w-4 mr-1.5" />
                Articles ({articles.length})
              </button>
            </nav>
          </div>

          <div className="flex gap-3">
            {activeTab === "bookings" && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-2 bg-white"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            )}
            {activeTab === "articles" && (
              <Button size="sm" className="gap-2 bg-sky-600 hover:bg-sky-700" onClick={openNewArticle} data-testid="new-article-btn">
                <Plus className="h-4 w-4" /> New Article
              </Button>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={activeTab === "bookings" ? "Search bookings..." : "Search messages..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>

        {/* Bookings Table */}
        {activeTab === "bookings" && (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Package</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-center">Guests</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Payment</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12 text-gray-500">
                          {searchQuery || statusFilter !== "all" ? "No bookings match your filters" : "No bookings yet"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredBookings.map((booking) => (
                        <React.Fragment key={booking.id}>
                          <TableRow
                            className={`cursor-pointer hover:bg-gray-50 transition-colors ${expandedBooking === booking.id ? "bg-sky-50/50" : ""}`}
                            onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                            data-testid={`booking-row-${booking.id}`}
                          >
                            <TableCell className="w-8 pl-4">
                              {expandedBooking === booking.id ? (
                                <ChevronUp className="h-4 w-4 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              )}
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-gray-900">{booking.name}</p>
                              <p className="text-xs text-gray-500">{booking.email}</p>
                            </TableCell>
                            <TableCell>
                              <span className="capitalize text-sm">{booking.package_type?.replace(/_/g, " ")}</span>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm">{new Date(booking.start_date).toLocaleDateString()}</p>
                            </TableCell>
                            <TableCell className="text-center">{booking.guests}</TableCell>
                            <TableCell className="text-right font-semibold">€{booking.total_price?.toFixed(2)}</TableCell>
                            <TableCell className="text-center">{getStatusBadge(booking.status)}</TableCell>
                            <TableCell className="text-center">{getPaymentBadge(booking.payment_status)}</TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex gap-1 justify-end">
                                {booking.status === "pending" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                    onClick={() => handleUpdateStatus(booking.id, "confirmed")}
                                    data-testid={`confirm-btn-${booking.id}`}
                                  >
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Confirm
                                  </Button>
                                )}
                                {booking.status === "confirmed" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs text-red-700 border-red-300 hover:bg-red-50"
                                    onClick={() => handleUpdateStatus(booking.id, "cancelled")}
                                    data-testid={`cancel-btn-${booking.id}`}
                                  >
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Cancel
                                  </Button>
                                )}
                                {booking.status === "cancelled" && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                    onClick={() => handleUpdateStatus(booking.id, "pending")}
                                  >
                                    Reopen
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>

                          {/* Expanded detail row */}
                          {expandedBooking === booking.id && (
                            <TableRow className="bg-sky-50/30">
                              <TableCell colSpan={9} className="p-0">
                                <div className="px-6 py-4 border-t border-sky-100">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                      <Phone className="h-4 w-4 text-gray-400" />
                                      <div>
                                        <p className="text-xs text-gray-500">Phone</p>
                                        <p className="font-medium">{booking.phone || "N/A"}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Mail className="h-4 w-4 text-gray-400" />
                                      <div>
                                        <p className="text-xs text-gray-500">Email</p>
                                        <p className="font-medium">{booking.email}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Globe className="h-4 w-4 text-gray-400" />
                                      <div>
                                        <p className="text-xs text-gray-500">Language</p>
                                        <p className="font-medium uppercase">{booking.language || "en"}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <CreditCard className="h-4 w-4 text-gray-400" />
                                      <div>
                                        <p className="text-xs text-gray-500">Lesson Type</p>
                                        <p className="font-medium capitalize">{booking.lesson_type || "N/A"}</p>
                                      </div>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Check-in</p>
                                      <p className="font-medium">{new Date(booking.start_date).toLocaleDateString()}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Check-out</p>
                                      <p className="font-medium">{booking.end_date ? new Date(booking.end_date).toLocaleDateString() : "N/A"}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Reference</p>
                                      <p className="font-bold text-blue-600" data-testid={`booking-ref-${booking.id}`}>{booking.booking_reference || "—"}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Booking ID</p>
                                      <p className="font-mono text-xs">{booking.id}</p>
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-500">Created</p>
                                      <p className="font-medium">{new Date(booking.created_at).toLocaleString()}</p>
                                    </div>
                                  </div>
                                  <div className="flex gap-2 mt-4 pt-3 border-t border-sky-100">
                                    {booking.payment_status === "pending" && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                        onClick={() => handleUpdatePaymentStatus(booking.id, "paid")}
                                        data-testid={`mark-paid-btn-${booking.id}`}
                                      >
                                        <DollarSign className="h-3 w-3 mr-1" />
                                        Mark as Paid
                                      </Button>
                                    )}
                                    {booking.payment_status === "paid" && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs"
                                        onClick={() => handleUpdatePaymentStatus(booking.id, "refunded")}
                                      >
                                        Refund
                                      </Button>
                                    )}
                                    <a
                                      href={`https://wa.me/${booking.phone?.replace(/[^0-9]/g, "")}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center h-8 px-3 text-xs font-medium border border-green-300 text-green-700 rounded-md hover:bg-green-50"
                                    >
                                      <Phone className="h-3 w-3 mr-1" />
                                      WhatsApp
                                    </a>
                                    <a
                                      href={`mailto:${booking.email}`}
                                      className="inline-flex items-center h-8 px-3 text-xs font-medium border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                                    >
                                      <Mail className="h-3 w-3 mr-1" />
                                      Email
                                    </a>
                                    <div className="ml-auto">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                                        onClick={() => handleDeleteBooking(booking.id)}
                                        data-testid={`delete-booking-btn-${booking.id}`}
                                      >
                                        <Trash2 className="h-3 w-3 mr-1" />
                                        Delete
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts */}
        {activeTab === "contacts" && (
          <div className="space-y-3">
            {filteredContacts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  {searchQuery ? "No messages match your search" : "No contact messages yet"}
                </CardContent>
              </Card>
            ) : (
              filteredContacts.map((contact) => (
                <Card key={contact.id} className="hover:shadow-md transition-shadow" data-testid={`contact-card-${contact.id}`}>
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-semibold text-gray-900">{contact.name}</p>
                          <Badge variant="outline" className="text-xs">{contact.language?.toUpperCase()}</Badge>
                          <span className="text-xs text-gray-400">
                            {new Date(contact.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex gap-4 text-sm text-gray-500 mb-3">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" /> {contact.email}
                          </span>
                          {contact.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" /> {contact.phone}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">{contact.message}</p>
                      </div>
                      <div className="flex gap-2 ml-4 flex-shrink-0">
                        <a
                          href={`mailto:${contact.email}?subject=Re: Your inquiry - Dakhla Kite Planet`}
                          className="inline-flex items-center h-8 px-3 text-xs font-medium border border-sky-300 text-sky-700 rounded-md hover:bg-sky-50"
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          Reply
                        </a>
                        {contact.phone && (
                          <a
                            href={`https://wa.me/${contact.phone.replace(/[^0-9]/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center h-8 px-3 text-xs font-medium border border-green-300 text-green-700 rounded-md hover:bg-green-50"
                          >
                            <Phone className="h-3 w-3 mr-1" />
                            WhatsApp
                          </a>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleDeleteContact(contact.id)}
                          data-testid={`delete-contact-btn-${contact.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Articles Tab */}
        {activeTab === "articles" && (
          <div>
            {editingArticle ? (
              /* Article Editor */
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold">{editingArticle === "new" ? "New Article" : "Edit Article"}</h3>
                    <Button variant="outline" size="sm" onClick={resetArticleForm}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                  </div>

                  {/* AI Generator */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <h4 className="font-semibold text-purple-800 flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4" /> AI Article Generator</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <Input placeholder="Topic (e.g., Best kite spots in Dakhla)" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} data-testid="ai-topic-input" />
                      <Input placeholder="Keywords (optional)" value={aiKeywords} onChange={(e) => setAiKeywords(e.target.value)} />
                      <Button onClick={handleAIGenerate} disabled={aiGenerating} className="bg-purple-600 hover:bg-purple-700" data-testid="ai-generate-btn">
                        {aiGenerating ? <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Generating...</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate</>}
                      </Button>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Title *</label>
                      <Input value={articleForm.title} onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} data-testid="article-title-input" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Slug *</label>
                      <Input value={articleForm.slug} onChange={(e) => setArticleForm({ ...articleForm, slug: e.target.value })} data-testid="article-slug-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
                      <select className="w-full border rounded-md px-3 py-2 text-sm" value={articleForm.category} onChange={(e) => setArticleForm({ ...articleForm, category: e.target.value })}>
                        <option value="kitesurfing">Kitesurfing</option>
                        <option value="travel">Travel</option>
                        <option value="activities">Activities</option>
                        <option value="tips">Tips</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Tags (comma-separated)</label>
                      <Input value={articleForm.tags} onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Featured Image URL</label>
                      <Input value={articleForm.featured_image} onChange={(e) => setArticleForm({ ...articleForm, featured_image: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Meta Title (SEO)</label>
                      <Input value={articleForm.meta_title} onChange={(e) => setArticleForm({ ...articleForm, meta_title: e.target.value })} maxLength={60} />
                      <p className="text-xs text-gray-400 mt-1">{articleForm.meta_title.length}/60</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Meta Description (SEO)</label>
                      <Input value={articleForm.meta_description} onChange={(e) => setArticleForm({ ...articleForm, meta_description: e.target.value })} maxLength={160} />
                      <p className="text-xs text-gray-400 mt-1">{articleForm.meta_description.length}/160</p>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Excerpt</label>
                    <textarea className="w-full border rounded-md px-3 py-2 text-sm h-20" value={articleForm.excerpt} onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })} />
                  </div>
                  <div className="mb-6">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Content (HTML) *</label>
                    <textarea className="w-full border rounded-md px-3 py-2 text-sm font-mono h-64" value={articleForm.content} onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })} data-testid="article-content-input" />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => { setArticleForm({ ...articleForm, status: "draft" }); setTimeout(handleSaveArticle, 0); }} disabled={savingArticle} variant="outline" data-testid="save-draft-btn">
                      <Save className="h-4 w-4 mr-2" /> Save Draft
                    </Button>
                    <Button onClick={() => { setArticleForm({ ...articleForm, status: "published" }); setTimeout(handleSaveArticle, 0); }} disabled={savingArticle} className="bg-emerald-600 hover:bg-emerald-700" data-testid="publish-btn">
                      <CheckCircle className="h-4 w-4 mr-2" /> Publish
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              /* Articles List */
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead>Published</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {articles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-12 text-gray-500">No articles yet. Create your first article!</TableCell>
                        </TableRow>
                      ) : (
                        articles.map((article) => (
                          <TableRow key={article.id} data-testid={`article-row-${article.id}`}>
                            <TableCell>
                              <p className="font-medium text-gray-900">{article.title}</p>
                              <p className="text-xs text-gray-400">/blog/{article.slug}</p>
                            </TableCell>
                            <TableCell><Badge variant="outline" className="capitalize text-xs">{article.category}</Badge></TableCell>
                            <TableCell className="text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${article.status === "published" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                                {article.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm text-gray-500">
                              {article.published_at ? new Date(article.published_at).toLocaleDateString() : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                <a href={`/blog/${article.slug}`} target="_blank" rel="noopener noreferrer">
                                  <Button size="sm" variant="outline" className="h-7 text-xs"><Eye className="h-3 w-3 mr-1" /> View</Button>
                                </a>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEditArticle(article)} data-testid={`edit-article-${article.id}`}>
                                  <Edit className="h-3 w-3 mr-1" /> Edit
                                </Button>
                                <Button size="sm" variant="outline" className={`h-7 text-xs ${article.status === "published" ? "text-amber-700" : "text-emerald-700"}`} onClick={() => handleTogglePublish(article)}>
                                  {article.status === "published" ? "Unpublish" : "Publish"}
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => handleDeleteArticle(article.id)} data-testid={`delete-article-${article.id}`}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
